/**
 * Shared timestamp formatting.
 *
 * The backend stores timestamps as UTC but SQLite drops the tzinfo, so the
 * API serializes them WITHOUT a trailing "Z" (e.g. "2026-07-07T16:00:00").
 * `new Date("2026-07-07T16:00:00")` parses that as *local* time, which shifts
 * every displayed time by the viewer's UTC offset — the "time is off" bug.
 *
 * These helpers fix that by treating any offset-less timestamp as UTC, then
 * rendering it in the operator-configured display time zone (Settings →
 * System → Display Time Zone, an IANA zone name like "America/New_York").
 */

import { formatDistanceToNowStrict } from "date-fns";

/**
 * The operator's configured clock (Settings → Date & Time), held here so it
 * does not have to be threaded through every call site.
 *
 * WHY THIS IS MODULE STATE AND NOT A PARAMETER. `tz` used to be an optional
 * argument, defaulting to the browser's zone when omitted. 56 files format
 * timestamps and only 7 ever passed it — so 49 files rendered in whatever
 * zone the viewer's laptop happened to be in, and the operator's setting did
 * nothing. An optional correctness argument is a correctness argument that
 * gets skipped; the only reliable default is the right one.
 *
 * Set once from BrandingContext when branding loads. Until then this is null
 * and formatting falls back to the browser zone, which is a better wrong
 * answer than UTC: it is at least the zone the person is sitting in.
 */
let configuredTz: string | null = null;

/** Called by BrandingContext when the operator's zone is known. */
export function setDisplayTimezone(tz: string | null | undefined): void {
  configuredTz = tz && tz.trim() ? tz.trim() : null;
}

/** The zone a timestamp should render in: explicit override, else configured. */
function zoneFor(tz?: string | null): string | undefined {
  return (tz && tz.trim()) || configuredTz || undefined;
}

/** True when the ISO string already carries a zone (Z or ±HH:MM). */
function hasZone(iso: string): boolean {
  // Look only at the time portion so a date-only "2026-07-07" isn't matched.
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso.trim());
}

/**
 * Parse a backend timestamp into a Date, assuming UTC when no zone is present.
 * Returns null for empty/invalid input.
 */
export function parseUtc(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  // Append "Z" to zone-less timestamps so the JS engine treats them as UTC.
  const normalized = hasZone(s) ? s : `${s.replace(" ", "T")}Z`;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/** Epoch millis for sorting; 0 for missing/invalid so blanks sort last-ish. */
export function utcMillis(value: string | null | undefined): number {
  const d = parseUtc(value);
  return d ? d.getTime() : 0;
}

/**
 * Format a backend timestamp in the operator's configured time zone.
 *
 * `tz` is an optional OVERRIDE, not a requirement — omitting it now gets the
 * configured zone rather than the browser's. See {@link setDisplayTimezone}.
 */
export function formatDateTime(
  value: string | null | undefined,
  tz?: string | null,
  fallback = "Never",
): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zoneFor(tz),
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    // Invalid tz name — render in the browser's local zone rather than throw.
    return d.toLocaleString();
  }
}

/** Date-only variant (no time-of-day) in the configured zone. */
export function formatDate(
  value: string | null | undefined,
  tz?: string | null,
  fallback = "—",
): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zoneFor(tz),
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

/**
 * Precise relative time ("12s ago", "3m ago", "2h ago") for a backend
 * timestamp. Two things this fixes over a raw
 * `formatDistanceToNow(new Date(value))`:
 *   1. Zone-less backend timestamps are parsed as UTC (via parseUtc), not as
 *      the viewer's local time — otherwise a just-now check-in on an EDT
 *      (UTC-4) browser renders as "in about 4 hours".
 *   2. `formatDistanceToNowStrict` gives a single, precise unit with no "about"
 *      fuzzing — which is what a "last seen" / "last polled" field needs.
 * Sub-45s ages collapse to "just now" so a healthy device doesn't flicker.
 */
export function formatRelative(
  value: string | null | undefined,
  fallback = "never",
): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  const secs = Math.abs(Date.now() - d.getTime()) / 1000;
  if (secs < 45) return "just now";
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

/**
 * The same answer as {@link formatRelative}, compressed for a table column.
 *
 *     "11 minutes ago"  ->  "11m ago"
 *     "2 hours ago"     ->  "2h ago"
 *     "4 days ago"      ->  "4d ago"
 *
 * WHY. In a `table-fixed` grid the When column competes with the one column
 * carrying the actual information. "11 minutes ago" needs ~8rem, which at
 * 1366px is 128px taken straight off Detail; narrowed to 6rem the text
 * overflowed its cell and rendered ON TOP of the first action button.
 * Screenshotted, not guessed. Compressed it needs about half.
 *
 * DERIVED from formatRelative rather than reimplemented, so the two cannot
 * disagree about the "just now" floor, about which unit applies at a
 * boundary, or about parsing the appliance's zone-less UTC. This only
 * rewrites units; anything unrecognised passes through untouched.
 */
export function formatRelativeShort(
  value: string | null | undefined,
  fallback = "never",
): string {
  const long = formatRelative(value, fallback);
  if (long === fallback || long === "just now") return long;
  return long
    .replace(/^about /, "")
    .replace(/(\d+)\s+seconds?/, "$1s")
    .replace(/(\d+)\s+minutes?/, "$1m")
    .replace(/(\d+)\s+hours?/, "$1h")
    .replace(/(\d+)\s+days?/, "$1d")
    .replace(/(\d+)\s+months?/, "$1mo")
    .replace(/(\d+)\s+years?/, "$1y");
}

/**
 * The list of IANA zone names for the Settings picker. Prefers the browser's
 * full list (`Intl.supportedValuesOf`), falling back to a curated set for
 * older engines. "UTC" is always first.
 */
export function supportedTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof intl.supportedValuesOf === "function") {
    try {
      const all = intl.supportedValuesOf("timeZone");
      return ["UTC", ...all.filter((z) => z !== "UTC")];
    } catch {
      /* fall through to curated list */
    }
  }
  return [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];
}

/** The viewer's own IANA zone, for a "use my zone" hint. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
